/**
 * @file Создание папки прямо в начале списка.
 *
 * Форма хранит введённое имя до подтверждения сервера. Ref текущего запроса
 * не допускает двойной отправки, а после закрытия восстанавливается фокус.
 */
import { useEffect, useId, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { FaFolder, FaFolderPlus } from 'react-icons/fa'

import { useLocale } from '../../i18n/useLocale'
import './NewFolder.css'
import { getFolderErrorCode, validateFolderName } from './folderName'
import type { FolderErrorCode } from './folderName'
import { folderMessages } from './messages'

export default function NewFolder({
    currentFolder,
    existingNames,
    disabled,
    onCreate,
    onRefresh,
    refreshFailed,
    toolbarActions,
}: {
    currentFolder: string
    existingNames: ReadonlySet<string>
    disabled: boolean
    onCreate: (name: string, signal: AbortSignal) => Promise<void>
    onRefresh: () => void
    refreshFailed: boolean
    toolbarActions?: ReactNode
}) {
    const { locale } = useLocale()
    const text = folderMessages[locale]

    // Ссылки на элементы нужны для фокуса, request — для отмены и блокировки.
    const id = useId()
    const trigger = useRef<HTMLButtonElement>(null)
    const input = useRef<HTMLInputElement>(null)

    // Управление отправкой и возвратом к кнопке после закрытия редактора.
    const request = useRef<AbortController | null>(null)
    const restoreFocus = useRef(false)

    // Видимое состояние формы и последнее успешное создание.
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [error, setError] = useState<FolderErrorCode | null>(null)
    const [pending, setPending] = useState(false)

    const [created, setCreated] = useState('')

    useEffect(() => () => request.current?.abort(), [])

    useEffect(() => {
        if (restoreFocus.current && !open && !pending && !disabled) {
            trigger.current?.focus()
            restoreFocus.current = false
        }
    }, [open, pending, disabled])

    function close() {
        if (request.current) return

        setOpen(false)
        setName('')
        setError(null)
        restoreFocus.current = true
    }

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        if (request.current || disabled) return

        const validationError = validateFolderName(name, existingNames)
        setError(validationError)
        if (validationError) {
            input.current?.focus()

            return
        }

        const controller = new AbortController()
        request.current = controller
        setPending(true)

        try {
            await onCreate(name.trim(), controller.signal)
            if (!controller.signal.aborted) {
                setCreated(name.trim())
                setName('')
                setOpen(false)
                restoreFocus.current = true
            }
        } catch (cause) {
            if (!controller.signal.aborted) setError(getFolderErrorCode(cause))
        } finally {
            request.current = null
            if (!controller.signal.aborted) setPending(false)
        }
    }

    return (
        <>
            {/* Кнопки остаются на месте; форма новой папки добавляется отдельным элементом ниже. */}
            <div className='new-folder-toolbar-block'>
                <div className='new-folder-toolbar'>
                    {toolbarActions}

                    <button
                        ref={trigger}
                        type='button'
                        className='new-folder-trigger'
                        disabled={disabled || pending}
                        aria-expanded={open}
                        aria-controls={`${id}-editor`}
                        onClick={() => {
                            setCreated('')
                            if (open) input.current?.focus()
                            else setOpen(true)
                        }}
                    >
                        <FaFolderPlus aria-hidden='true' />
                        {text.newFolder}
                    </button>

                    <button
                        type='button'
                        disabled={disabled || pending}
                        onClick={onRefresh}
                    >
                        {text.refresh}
                    </button>
                </div>

                {refreshFailed && (
                    <p className='new-folder-error' role='alert'>
                        {text.refreshFailed}
                    </p>
                )}
                {created && (
                    <p className='new-folder-success' role='status'>
                        {text.created} {created}
                    </p>
                )}
            </div>

            {/* Редактор живёт только пока открыт. Ошибка сохраняет имя, успех закрывает форму. */}
            {open && (
                <form
                    id={`${id}-editor`}
                    className='new-folder-editor'
                    onSubmit={submit}
                    noValidate
                    aria-busy={pending}
                    onKeyDown={(event) => {
                        if (event.nativeEvent.isComposing) {
                            if (event.key === 'Enter') event.preventDefault()

                            return
                        }

                        if (event.key === 'Escape' && !pending) {
                            event.preventDefault()
                            close()
                        }
                    }}
                >
                    <FaFolder className='new-folder-icon' aria-hidden='true' />

                    <div className='new-folder-field'>
                        <label
                            className='new-folder-sr-only'
                            htmlFor={`${id}-name`}
                        >
                            {text.name}
                        </label>
                        <input
                            ref={input}
                            id={`${id}-name`}
                            name='folderName'
                            autoFocus
                            autoComplete='off'
                            value={name}
                            disabled={pending}
                            aria-invalid={Boolean(error)}
                            aria-describedby={`${id}-location${error ? ` ${id}-error` : ''}`}
                            onChange={(event) => {
                                setName(event.target.value)
                                setError(null)
                            }}
                        />

                        <p id={`${id}-location`} className='new-folder-sr-only'>
                            {text.location}: <span>{currentFolder}</span>
                        </p>

                        {error && (
                            <p
                                id={`${id}-error`}
                                className='new-folder-error'
                                role='alert'
                            >
                                {text[error]}
                            </p>
                        )}
                    </div>

                    <div className='new-folder-actions'>
                        <button
                            type='submit'
                            className='new-folder-submit'
                            disabled={pending || disabled}
                        >
                            {pending ? text.creating : text.create}
                        </button>

                        <button
                            type='button'
                            disabled={pending}
                            onClick={close}
                        >
                            {text.cancel}
                        </button>
                    </div>
                </form>
            )}
        </>
    )
}
